/**
 * Dashboard-optimized EHR XML to JSON parser.
 * Outputs JSON that directly matches the dashboard's expected format.
 * Clinical notes and non-numeric data are ignored.
 */

// =============================================================================
// REFERENCE RANGES DATABASE
// =============================================================================

interface ReferenceRange {
  low: number;
  high: number;
  unit: string;
  name: string;
  criticalLow?: number;
  criticalHigh?: number;
}

const REFERENCE_RANGES: Record<string, ReferenceRange> = {
  // Metabolic Panel (Updated with Cleveland HeartLab ranges)
  'LOINC/2345-7': { low: 65, high: 99, unit: 'mg/dL', name: 'Glucose', criticalLow: 50, criticalHigh: 400 },
  'LOINC/2160-0': { low: 0.6, high: 1.2, unit: 'mg/dL', name: 'Creatinine', criticalHigh: 10 },
  'LOINC/3094-0': { low: 7, high: 20, unit: 'mg/dL', name: 'BUN' },
  'LOINC/2823-3': { low: 3.5, high: 5.0, unit: 'mmol/L', name: 'Potassium', criticalLow: 2.5, criticalHigh: 6.5 },
  'LOINC/2951-2': { low: 136, high: 145, unit: 'mmol/L', name: 'Sodium', criticalLow: 120, criticalHigh: 160 },
  'LOINC/2075-0': { low: 98, high: 106, unit: 'mmol/L', name: 'Chloride' },
  'LOINC/2028-9': { low: 23, high: 29, unit: 'mmol/L', name: 'CO2' },
  'LOINC/17861-6': { low: 8.5, high: 10.5, unit: 'mg/dL', name: 'Calcium' },

  // Liver Function
  'LOINC/1742-6': { low: 7, high: 56, unit: 'U/L', name: 'ALT', criticalHigh: 1000 },
  'LOINC/1920-8': { low: 10, high: 40, unit: 'U/L', name: 'AST', criticalHigh: 1000 },
  'LOINC/6768-6': { low: 44, high: 147, unit: 'U/L', name: 'Alkaline Phosphatase' },
  'LOINC/1975-2': { low: 0.1, high: 1.2, unit: 'mg/dL', name: 'Total Bilirubin', criticalHigh: 15 },
  'LOINC/2885-2': { low: 6.0, high: 8.3, unit: 'g/dL', name: 'Total Protein' },
  'LOINC/1751-7': { low: 3.5, high: 5.0, unit: 'g/dL', name: 'Albumin' },

  // Lipid Panel (Updated with Cleveland HeartLab ranges)
  'LOINC/2093-3': { low: 0, high: 200, unit: 'mg/dL', name: 'Total Cholesterol' }, // <200 optimal
  'LOINC/2571-8': { low: 0, high: 150, unit: 'mg/dL', name: 'Triglycerides' }, // <150 optimal, 150-199 moderate, ≥200 high
  'LOINC/2085-9': { low: 40, high: 999, unit: 'mg/dL', name: 'HDL Cholesterol' }, // ≥40 optimal, <40 high risk
  'LOINC/13457-7': { low: 0, high: 100, unit: 'mg/dL', name: 'LDL Cholesterol' }, // <100 optimal, 100-129 moderate, ≥130 high
  'LOINC/18262-6': { low: 0, high: 100, unit: 'mg/dL', name: 'LDL Cholesterol' },
  'LOINC/1869-7': { low: 0, high: 90, unit: 'mg/dL', name: 'Apolipoprotein B' }, // <90 optimal, 90-129 moderate, ≥130 high

  // CBC
  'LOINC/6690-2': { low: 4.5, high: 11.0, unit: 'K/uL', name: 'WBC', criticalLow: 2, criticalHigh: 30 },
  'LOINC/789-8': { low: 4.0, high: 5.5, unit: 'M/uL', name: 'RBC' },
  'LOINC/718-7': { low: 12.0, high: 16.0, unit: 'g/dL', name: 'Hemoglobin', criticalLow: 7, criticalHigh: 20 },
  'LOINC/4544-3': { low: 36, high: 46, unit: '%', name: 'Hematocrit', criticalLow: 20, criticalHigh: 60 },
  'LOINC/777-3': { low: 150, high: 400, unit: 'K/uL', name: 'Platelets', criticalLow: 50, criticalHigh: 1000 },

  // Diabetes (Updated with Cleveland HeartLab ranges)
  'LOINC/4548-4': { low: 0, high: 5.7, unit: '%', name: 'Hemoglobin A1c' }, // <5.7 optimal, 5.7-6.4 moderate, >6.4 high

  // Thyroid
  'LOINC/3016-3': { low: 0.4, high: 4.0, unit: 'mIU/L', name: 'TSH' },
  'LOINC/3026-2': { low: 0.8, high: 1.8, unit: 'ng/dL', name: 'Free T4' },

  // Kidney
  'LOINC/33914-3': { low: 60, high: 120, unit: 'mL/min', name: 'eGFR' },
};

// Vital sign LOINC codes
const VITAL_CODES: Record<string, string> = {
  'LOINC/8480-6': 'systolic',
  'LOINC/8462-4': 'diastolic',
  'LOINC/8867-4': 'heartRate',
  'LOINC/9279-1': 'respiratoryRate',
  'LOINC/8310-5': 'temperature',
  'LOINC/29463-7': 'weight',
  'LOINC/8302-2': 'height',
  'LOINC/39156-5': 'bmi',
  'LOINC/59408-5': 'oxygenSaturation',
  'LOINC/8478-0': 'meanBP',
};

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface DashboardData {
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    mrn: string;
    gender: string;
    ethnicity: string;
    riskLevel: 'low' | 'medium' | 'high';
  };
  medications: Array<{
    id: string;
    name: string;
    dosage: string;
    code: string;
    prescribedDate: string;
    status: 'active' | 'discontinued';
  }>;
  labResults: Array<{
    id: string;
    name: string;
    value: number;
    unit: string;
    referenceRange: string;
    status: 'normal' | 'abnormal' | 'critical';
    date: string;
  }>;
  vitals: {
    bloodPressure?: { systolic: number; diastolic: number; date: string };
    heartRate?: { value: number; unit: string; date: string };
    weight?: { value: number; unit: string; date: string };
    bmi?: { value: number; date: string };
  };
  healthMetrics: {
    metabolicSyndrome: { risk: string; score: number; factors: string[] };
    hypertension: { systolic: number; diastolic: number; risk: string };
    liverEnzymes: { alt: number; ast: number; risk: string };
    obesity: { bmi: number; risk: string };
    heartDisease: { cholesterol: { total: number; ldl: number; hdl: number }; risk: string };
  };
  historicalData?: {
    weight?: HistoricalMeasurement[];
    bmi?: HistoricalMeasurement[];
    bloodPressure?: HistoricalVital[];
    heartRate?: HistoricalMeasurement[];
    glucose?: HistoricalMeasurement[];
    ldlCholesterol?: HistoricalMeasurement[];
    hdlCholesterol?: HistoricalMeasurement[];
    totalCholesterol?: HistoricalMeasurement[];
    apoB?: HistoricalMeasurement[];
    hba1c?: HistoricalMeasurement[];
    triglycerides?: HistoricalMeasurement[];
  };
}

export interface HistoricalMeasurement {
  date: string; // ISO date string
  value: number;
}

export interface HistoricalVital {
  date: string;
  systolic?: number;
  diastolic?: number;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function parseDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const trimmed = dateStr.trim();
  const date = new Date(trimmed);
  return !isNaN(date.getTime()) ? date.toISOString() : trimmed;
}

function formatDateShort(isoDate: string | null): string {
  if (!isoDate) return 'Unknown';
  return isoDate.split('T')[0];
}

/**
 * Special handler for semaglutide medications which have unique formatting.
 * Examples:
 * - "1 MG Dose 1.5 ML semaglutide 1.34 MG/ML Pen Injector"
 * - "0.25 MG, 0.5 MG Dose 1.5 ML semaglutide 1.34 MG/ML Pen Injector"
 */
function parseSemaglutide(fullName: string): { simpleName: string; dosage: string; form: string } | null {
  if (!fullName.toLowerCase().includes('semaglutide')) {
    return null;
  }

  // Extract the dose from the beginning (e.g., "1 MG" or "0.25 MG, 0.5 MG")
  // Pattern matches doses like "1 MG", "0.25 MG", or "0.25 MG, 0.5 MG"
  const dosePattern = /^((?:\d+\.?\d*\s*MG(?:\s*,\s*)?)+)\s*Dose/i;
  const doseMatch = fullName.match(dosePattern);

  let dosage: string;
  if (doseMatch) {
    dosage = doseMatch[1].trim().toUpperCase();
    // Clean up formatting (remove trailing comma if present)
    dosage = dosage.replace(/,\s*$/, '').trim();
  } else {
    // Fallback: try to extract concentration from "X.XX MG/ML"
    const concPattern = /semaglutide\s+(\d+\.?\d*)\s*MG\/ML/i;
    const concMatch = fullName.match(concPattern);
    dosage = concMatch ? `${concMatch[1]} MG/ML` : 'N/A';
  }

  // Determine form (Pen Injector, etc.)
  const form = fullName.toLowerCase().includes('pen injector') ? 'Pen Injector' : 'Injectable';

  return {
    simpleName: 'Semaglutide',
    dosage,
    form
  };
}

function parseMedicationName(fullName: string): { simpleName: string; dosage: string; form: string } {
  // Check for special medication handlers first
  const semaglutideResult = parseSemaglutide(fullName);
  if (semaglutideResult) {
    return semaglutideResult;
  }

  const pattern = /^(.+?)\s+(\d+\.?\d*)\s*(MG|MCG|UNIT|ML|G|%|MEQ)(?:\s*\/\s*(?:MG|ML|ACTUAT))?\s*(.*)$/i;
  const match = fullName.match(pattern);

  if (match) {
    const drugName = match[1].trim();
    const strength = match[2];
    const unit = match[3].toUpperCase();
    const form = match[4]?.trim() || '';

    // Title case the drug name
    const simpleName = drugName.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    return { simpleName, dosage: `${strength} ${unit}`, form };
  }

  const words = fullName.split(' ');
  return {
    simpleName: words[0] ? words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase() : fullName,
    dosage: '',
    form: ''
  };
}

function getLabStatus(value: number | null, code: string): 'normal' | 'abnormal' | 'critical' {
  if (value === null) return 'normal';

  const ref = REFERENCE_RANGES[code];
  if (!ref) return 'normal';

  if (ref.criticalLow !== undefined && value < ref.criticalLow) return 'critical';
  if (ref.criticalHigh !== undefined && value > ref.criticalHigh) return 'critical';
  if (value < ref.low || value > ref.high) return 'abnormal';

  return 'normal';
}

function getReferenceRangeString(code: string): string {
  const ref = REFERENCE_RANGES[code];
  return ref ? `${ref.low}-${ref.high} ${ref.unit}` : 'N/A';
}

function simplifyLabName(name: string, code: string): string {
  const ref = REFERENCE_RANGES[code];
  if (ref) return ref.name;

  return name
    .replace(/\s*\[.*?\]\s*/g, ' ')
    .replace(/\s+in\s+(Serum|Plasma|Blood|Urine).*$/i, '')
    .replace(/\s+by\s+.*$/i, '')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// =============================================================================
// MAIN PARSER
// =============================================================================

export function parseXmlToJson(xmlContent: string): DashboardData {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlContent, 'text/xml');

  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    throw new Error(`XML parsing error: ${parserError.textContent}`);
  }

  const root = doc.documentElement;
  const personId = root.getAttribute('person_id') || 'Unknown';

  // Initialize data structures
  const demographics: { birthdate?: string; gender?: string; ethnicity?: string } = {};
  const rawMedications = new Map<string, Array<{ name: string; value: string; timestamp: string | null }>>();
  const rawLabs = new Map<string, { code: string; name: string; value: number | null; unit: string; timestamp: string | null }>();
  const vitalsData = new Map<string, { value: number; unit: string; timestamp: string | null }>();

  // Historical data arrays (collect ALL measurements)
  const historicalWeight: HistoricalMeasurement[] = [];
  const historicalBMI: HistoricalMeasurement[] = [];
  const historicalBloodPressure: Map<string, { systolic?: number; diastolic?: number }> = new Map();
  const historicalHeartRate: HistoricalMeasurement[] = [];
  const historicalGlucose: HistoricalMeasurement[] = [];
  const historicalLDL: HistoricalMeasurement[] = [];
  const historicalHDL: HistoricalMeasurement[] = [];
  const historicalTotal: HistoricalMeasurement[] = [];
  const historicalApoB: HistoricalMeasurement[] = [];
  const historicalHbA1c: HistoricalMeasurement[] = [];
  const historicalTriglycerides: HistoricalMeasurement[] = [];

  // Process all encounters
  const encounters = root.querySelectorAll('encounter');

  encounters.forEach(encounter => {
    // Extract demographics
    const personElem = encounter.querySelector('person');
    if (personElem && !demographics.birthdate) {
      const birthdate = personElem.querySelector('birthdate')?.textContent?.trim();
      if (birthdate) demographics.birthdate = birthdate;

      const demoElem = personElem.querySelector('demographics');
      if (demoElem) {
        const ethnicity = demoElem.querySelector('ethnicity')?.textContent?.trim();
        const gender = demoElem.querySelector('gender')?.textContent?.trim();
        if (ethnicity) demographics.ethnicity = ethnicity;
        if (gender) demographics.gender = gender;
      }
    }

    // Process events
    const entries = encounter.querySelectorAll('events > entry');

    entries.forEach(entry => {
      const timestamp = parseDate(entry.getAttribute('timestamp'));

      const events = entry.querySelectorAll('event');
      events.forEach(event => {
        // Skip notes
        if (event.getAttribute('note_id')) return;

        const code = event.getAttribute('code') || '';
        const name = event.getAttribute('name') || '';
        const value = event.textContent?.trim() || '';
        const unit = event.getAttribute('unit') || '';

        // Medications (RxNorm)
        if (code.includes('RxNorm') || code.includes('NDC')) {
          if (!rawMedications.has(code)) {
            rawMedications.set(code, []);
          }
          rawMedications.get(code)!.push({ name, value, timestamp });
        }
        // LOINC codes (labs and vitals)
        else if (code.includes('LOINC')) {
          // Vital signs
          if (VITAL_CODES[code]) {
            const vitalType = VITAL_CODES[code];
            const numericValue = parseFloat(value);

            if (!isNaN(numericValue)) {
              const existing = vitalsData.get(vitalType);
              if (!existing || (timestamp && existing.timestamp && timestamp > existing.timestamp)) {
                vitalsData.set(vitalType, { value: numericValue, unit, timestamp });
              }

              // Collect historical data
              if (timestamp) {
                const dateStr = formatDateShort(timestamp);

                if (vitalType === 'weight') {
                  let lbs = numericValue;
                  if (unit.toLowerCase().includes('kg')) lbs = numericValue * 2.20462;
                  else if (unit.toLowerCase().includes('oz')) lbs = numericValue / 16;

                  if (lbs > 50 && lbs < 600) {
                    historicalWeight.push({ date: dateStr, value: Math.round(lbs * 10) / 10 });
                  }
                } else if (vitalType === 'bmi') {
                  if (numericValue > 10 && numericValue < 100) {
                    historicalBMI.push({ date: dateStr, value: Math.round(numericValue * 10) / 10 });
                  }
                } else if (vitalType === 'systolic' || vitalType === 'diastolic') {
                  if (!historicalBloodPressure.has(timestamp)) {
                    historicalBloodPressure.set(timestamp, {});
                  }
                  const bpEntry = historicalBloodPressure.get(timestamp)!;
                  if (vitalType === 'systolic') bpEntry.systolic = Math.round(numericValue);
                  else bpEntry.diastolic = Math.round(numericValue);
                } else if (vitalType === 'heartRate') {
                  if (numericValue > 30 && numericValue < 250) {
                    historicalHeartRate.push({ date: dateStr, value: Math.round(numericValue) });
                  }
                }
              }
            }
          }
          // Lab results
          else {
            const numericValue = value ? parseFloat(value) : null;
            const existing = rawLabs.get(code);

            if (!existing || (timestamp && existing.timestamp && timestamp > existing.timestamp)) {
              rawLabs.set(code, {
                code,
                name,
                value: numericValue !== null && !isNaN(numericValue) ? numericValue : null,
                unit,
                timestamp
              });
            }

            // Collect historical lab data
            if (numericValue !== null && !isNaN(numericValue) && timestamp) {
              const dateStr = formatDateShort(timestamp);

              if (code === 'LOINC/2345-7') historicalGlucose.push({ date: dateStr, value: numericValue });
              else if (code === 'LOINC/13457-7' || code === 'LOINC/18262-6') historicalLDL.push({ date: dateStr, value: numericValue });
              else if (code === 'LOINC/2085-9') historicalHDL.push({ date: dateStr, value: numericValue });
              else if (code === 'LOINC/2093-3') historicalTotal.push({ date: dateStr, value: numericValue });
              else if (code === 'LOINC/1869-7') historicalApoB.push({ date: dateStr, value: numericValue });
              else if (code === 'LOINC/4548-4') historicalHbA1c.push({ date: dateStr, value: numericValue });
              else if (code === 'LOINC/2571-8') historicalTriglycerides.push({ date: dateStr, value: numericValue });
            }
          }
        }
      });
    });
  });

  // Process medications
  const medications: DashboardData['medications'] = [];
  let medId = 1;

  rawMedications.forEach((events, code) => {
    if (events.length === 0) return;

    const sortedEvents = events
      .filter(e => e.timestamp)
      .sort((a, b) => new Date(a.timestamp!).getTime() - new Date(b.timestamp!).getTime());

    if (sortedEvents.length === 0) return;

    const parsed = parseMedicationName(sortedEvents[0].name);

    let startDate: string | null = null;
    let endDate: string | null = null;

    for (const event of sortedEvents) {
      const val = event.value.toLowerCase();
      if (val.includes('start') && !startDate) startDate = event.timestamp;
      if (val.includes('end')) endDate = event.timestamp;
    }

    let status: 'active' | 'discontinued' = 'active';
    if (sortedEvents.some(e => e.value.toLowerCase() === 'start|end')) {
      status = 'discontinued';
      if (!startDate) startDate = sortedEvents[0].timestamp;
    } else if (endDate) {
      status = 'discontinued';
    } else {
      if (!startDate) startDate = sortedEvents[0].timestamp;
    }

    medications.push({
      id: `med-${medId++}`,
      name: parsed.simpleName,
      dosage: `${parsed.dosage} ${parsed.form}`.trim() || 'N/A',
      code: code,
      prescribedDate: formatDateShort(startDate),
      status
    });
  });

  // Sort medications: active first, then by name
  medications.sort((a, b) => {
    if (a.status === 'active' && b.status !== 'active') return -1;
    if (a.status !== 'active' && b.status === 'active') return 1;
    return a.name.localeCompare(b.name);
  });

  // Process lab results
  const labResults: DashboardData['labResults'] = [];
  let labId = 1;

  rawLabs.forEach((lab) => {
    if (lab.value === null) return;

    labResults.push({
      id: `lab-${labId++}`,
      name: simplifyLabName(lab.name, lab.code),
      value: lab.value,
      unit: lab.unit || REFERENCE_RANGES[lab.code]?.unit || '',
      referenceRange: getReferenceRangeString(lab.code),
      status: getLabStatus(lab.value, lab.code),
      date: formatDateShort(lab.timestamp)
    });
  });

  // Sort lab results: critical first, then abnormal, then normal
  const statusOrder = { critical: 0, abnormal: 1, normal: 2 };
  labResults.sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);

  // Sort all historical data by date (oldest to newest)
  const sortByDate = (a: HistoricalMeasurement, b: HistoricalMeasurement) =>
    new Date(a.date).getTime() - new Date(b.date).getTime();

  historicalWeight.sort(sortByDate);
  historicalBMI.sort(sortByDate);
  historicalHeartRate.sort(sortByDate);
  historicalGlucose.sort(sortByDate);
  historicalLDL.sort(sortByDate);
  historicalHDL.sort(sortByDate);
  historicalTotal.sort(sortByDate);
  historicalApoB.sort(sortByDate);
  historicalHbA1c.sort(sortByDate);
  historicalTriglycerides.sort(sortByDate);

  // Convert blood pressure map to array (only include complete pairs)
  const historicalBP: HistoricalVital[] = [];
  historicalBloodPressure.forEach((bp, timestamp) => {
    if (bp.systolic !== undefined && bp.diastolic !== undefined) {
      historicalBP.push({
        date: formatDateShort(timestamp),
        systolic: bp.systolic,
        diastolic: bp.diastolic
      });
    }
  });
  historicalBP.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Build vitals object
  const vitals: DashboardData['vitals'] = {};

  const systolicData = vitalsData.get('systolic');
  const diastolicData = vitalsData.get('diastolic');
  if (systolicData) {
    vitals.bloodPressure = {
      systolic: systolicData.value,
      diastolic: diastolicData?.value || 80,
      date: formatDateShort(systolicData.timestamp)
    };
  }

  const heartRateData = vitalsData.get('heartRate');
  if (heartRateData) {
    vitals.heartRate = {
      value: heartRateData.value,
      unit: 'bpm',
      date: formatDateShort(heartRateData.timestamp)
    };
  }

  const weightData = vitalsData.get('weight');
  if (weightData) {
    vitals.weight = {
      value: weightData.value,
      unit: weightData.unit || 'kg',
      date: formatDateShort(weightData.timestamp)
    };
  }

  const bmiData = vitalsData.get('bmi');
  if (bmiData) {
    vitals.bmi = {
      value: bmiData.value,
      date: formatDateShort(bmiData.timestamp)
    };
  }

  // Build health metrics
  const healthMetrics: DashboardData['healthMetrics'] = {
    metabolicSyndrome: { risk: 'low', score: 0, factors: [] },
    hypertension: { systolic: 120, diastolic: 80, risk: 'low' },
    liverEnzymes: { alt: 25, ast: 25, risk: 'low' },
    obesity: { bmi: 24, risk: 'low' },
    heartDisease: { cholesterol: { total: 180, ldl: 100, hdl: 50 }, risk: 'low' }
  };

  // Update from vitals
  if (vitals.bloodPressure) {
    const { systolic, diastolic } = vitals.bloodPressure;
    const risk = systolic >= 140 || diastolic >= 90 ? 'high' :
                 systolic >= 130 || diastolic >= 85 ? 'medium' : 'low';
    healthMetrics.hypertension = { systolic, diastolic, risk };
  }

  if (vitals.bmi) {
    const bmi = vitals.bmi.value;
    const risk = bmi >= 30 ? 'high' : bmi >= 25 ? 'medium' : 'low';
    healthMetrics.obesity = { bmi, risk };
  }

  // Update from lab results
  for (const lab of labResults) {
    if (lab.name === 'ALT') healthMetrics.liverEnzymes.alt = lab.value;
    else if (lab.name === 'AST') healthMetrics.liverEnzymes.ast = lab.value;
    else if (lab.name === 'Total Cholesterol') healthMetrics.heartDisease.cholesterol.total = lab.value;
    else if (lab.name === 'LDL Cholesterol') healthMetrics.heartDisease.cholesterol.ldl = lab.value;
    else if (lab.name === 'HDL Cholesterol') healthMetrics.heartDisease.cholesterol.hdl = lab.value;
  }

  // Calculate liver enzyme risk
  const { alt, ast } = healthMetrics.liverEnzymes;
  if (alt > 56 || ast > 40) {
    healthMetrics.liverEnzymes.risk = alt > 100 || ast > 100 ? 'high' : 'medium';
  }

  // Calculate heart disease risk
  const { total, ldl, hdl } = healthMetrics.heartDisease.cholesterol;
  if (total > 240 || ldl > 160 || hdl < 40) {
    healthMetrics.heartDisease.risk = 'high';
  } else if (total > 200 || ldl > 130 || hdl < 50) {
    healthMetrics.heartDisease.risk = 'medium';
  }

  // Calculate overall risk
  const risks = [
    healthMetrics.metabolicSyndrome.risk,
    healthMetrics.hypertension.risk,
    healthMetrics.liverEnzymes.risk,
    healthMetrics.obesity.risk,
    healthMetrics.heartDisease.risk
  ];

  const riskLevel: 'low' | 'medium' | 'high' =
    risks.includes('high') ? 'high' :
    risks.filter(r => r === 'medium').length >= 2 || risks.includes('medium') ? 'medium' : 'low';

  return {
    patient: {
      id: personId,
      firstName: 'Patient',
      lastName: personId,
      dateOfBirth: formatDateShort(demographics.birthdate || null),
      mrn: personId,
      gender: demographics.gender || 'Unknown',
      ethnicity: demographics.ethnicity || 'Unknown',
      riskLevel
    },
    medications,
    labResults,
    vitals,
    healthMetrics,
    historicalData: {
      weight: historicalWeight.length > 0 ? historicalWeight : undefined,
      bmi: historicalBMI.length > 0 ? historicalBMI : undefined,
      bloodPressure: historicalBP.length > 0 ? historicalBP : undefined,
      heartRate: historicalHeartRate.length > 0 ? historicalHeartRate : undefined,
      glucose: historicalGlucose.length > 0 ? historicalGlucose : undefined,
      ldlCholesterol: historicalLDL.length > 0 ? historicalLDL : undefined,
      hdlCholesterol: historicalHDL.length > 0 ? historicalHDL : undefined,
      totalCholesterol: historicalTotal.length > 0 ? historicalTotal : undefined,
      apoB: historicalApoB.length > 0 ? historicalApoB : undefined,
      hba1c: historicalHbA1c.length > 0 ? historicalHbA1c : undefined,
      triglycerides: historicalTriglycerides.length > 0 ? historicalTriglycerides : undefined,
    }
  };
}

// Export types for use in other files
export type { DashboardData as ParsedEhrData };
